import { Box, Flex, Grid, Text, VStack } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { LuChevronRight as ChevronRight } from 'react-icons/lu'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import { useChartTheme } from '../../../hooks/useChartTheme'
import { brand, themeColors } from '../../../theme'
import { mineralBreakdown, monthlyTonnage } from '../../../data/mockEstadisticas'
import CustomTooltip from '../components/CustomTooltip'
import ChartCard from '../components/ChartCard'
import TrendIndicator from '../components/TrendIndicator'

const MotionBox = motion.create(Box)

const mineralTrends: Record<string, { trend: number; dir: 'up' | 'down' }> = {
  Cobre: { trend: 14.2, dir: 'up' },
  Oro: { trend: 8.7, dir: 'up' },
  Hierro: { trend: 11.5, dir: 'up' },
  Zinc: { trend: 6.3, dir: 'up' },
  Plata: { trend: 3.1, dir: 'up' },
  Otros: { trend: 1.8, dir: 'down' },
}

export default function DetalleMineralesPage() {
  const { axis, grid } = useChartTheme()
  const colors = themeColors
  const navigate = useNavigate()

  return (
    <VStack gap={6} alignItems="stretch">
      {/* Breadcrumb */}
      <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <Flex gap={2} alignItems="center" mb={2}>
          <Text fontSize="sm" color={brand.green} cursor="pointer" _hover={{ textDecoration: 'underline' }} onClick={() => navigate('/app/estadisticas')}>
            Estadisticas
          </Text>
          <ChevronRight size={14} color={colors.textMuted} />
          <Text fontSize="sm" color={colors.text} fontWeight="600">
            Minerales
          </Text>
        </Flex>
        <Text fontSize="2xl" fontWeight="bold" color={colors.text}>
          Detalle por Mineral
        </Text>
        <Text fontSize="10px" textTransform="uppercase" letterSpacing="0.2em" fontWeight="bold" color={colors.textMuted} mt={1}>
          {mineralBreakdown.length} tipos de minerales
        </Text>
      </MotionBox>

      {/* Mineral Cards */}
      <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={4}>
        {mineralBreakdown.map((mineral, index) => {
          const t = mineralTrends[mineral.tipo] || { trend: 0, dir: 'up' as const }
          return (
            <MotionBox
              key={mineral.tipo}
              bg={colors.bgCard}
              border="1px solid"
              borderColor={colors.border}
              borderRadius="2xl"
              p={5}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              boxShadow="none"
            >
              <Flex justifyContent="space-between" alignItems="center" mb={3}>
                <Flex alignItems="center" gap={2}>
                  <Box w="12px" h="12px" borderRadius="sm" bg={mineral.color} />
                  <Text fontSize="md" fontWeight="bold" color={colors.text}>
                    {mineral.tipo}
                  </Text>
                </Flex>
                <Text fontSize="xs" color={colors.textMuted}>
                  {mineral.proveedores} proveedores
                </Text>
              </Flex>
              <Text fontSize="xl" fontWeight="bold" color={colors.text} mb={1}>
                {mineral.tonelaje.toLocaleString()} TM
              </Text>
              <Flex justifyContent="space-between" alignItems="center">
                <Text fontSize="sm" color={colors.textSecondary}>
                  {mineral.porcentaje}% del total
                </Text>
                <TrendIndicator value={t.trend} direction={t.dir} />
              </Flex>
            </MotionBox>
          )
        })}
      </Grid>

      {/* Evolution Chart */}
      <ChartCard title="Evolución Mensual por Mineral" subtitle="Tendencia de los últimos 7 meses" delay={0.3}>
        <Box h="360px">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={monthlyTonnage}>
              <defs>
                {[
                  { key: 'cobre', color: '#1A7A4C' },
                  { key: 'oro', color: '#2a8a87' },
                  { key: 'hierro', color: '#EAB308' },
                  { key: 'zinc', color: '#3d6fa8' },
                  { key: 'plata', color: '#b5aa9d' },
                ].map((m) => (
                  <linearGradient key={m.key} id={`det-${m.key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={m.color} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={m.color} stopOpacity={0.05} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid stroke={grid.stroke} strokeDasharray={grid.strokeDasharray} />
              <XAxis dataKey="month" stroke={axis.stroke} tick={axis.tick} />
              <YAxis stroke={axis.stroke} tick={axis.tick} tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip content={<CustomTooltip formatter={(v) => `${v.toLocaleString()} TM`} />} />
              <Legend wrapperStyle={{ fontSize: 11, fontFamily: "'Instrument Sans', sans-serif" }} />
              {[
                { key: 'cobre', label: 'Cobre', color: '#1A7A4C' },
                { key: 'oro', label: 'Oro', color: '#2a8a87' },
                { key: 'hierro', label: 'Hierro', color: '#EAB308' },
                { key: 'zinc', label: 'Zinc', color: '#3d6fa8' },
                { key: 'plata', label: 'Plata', color: '#b5aa9d' },
              ].map((m) => (
                <Area key={m.key} type="monotone" dataKey={m.key} name={m.label} stroke={m.color} fill={`url(#det-${m.key})`} strokeWidth={2} />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </ChartCard>

      {/* Detailed Table */}
      <MotionBox
        bg={colors.bgCard}
        border="1px solid"
        borderColor={colors.border}
        borderRadius="2xl"
        overflow="hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.35 }}
        boxShadow="none"
      >
        <Box p={6} borderBottom="1px solid" borderColor={colors.border}>
          <Text fontSize="lg" fontWeight="bold" color={colors.text}>
            Resumen por Mineral
          </Text>
        </Box>
        <Box overflowX="auto">
          <Box as="table" width="100%" textAlign="left">
            <Box as="thead">
              <Box as="tr" bg={colors.bgHover}>
                {['Mineral', 'Tonelaje', '% Total', 'Proveedores', 'Tendencia'].map((h) => (
                  <Box as="th" key={h} px={6} py={4} fontSize="10px" textTransform="uppercase" letterSpacing="0.2em" fontWeight="bold" color={colors.textMuted}>
                    {h}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box as="tbody">
              {mineralBreakdown.map((m) => {
                const t = mineralTrends[m.tipo] || { trend: 0, dir: 'up' as const }
                return (
                  <Box as="tr" key={m.tipo} borderBottom="1px solid" borderColor={colors.border} _hover={{ bg: colors.bgHover }}>
                    <Box as="td" px={6} py={4}>
                      <Flex alignItems="center" gap={2}>
                        <Box w="10px" h="10px" borderRadius="sm" bg={m.color} />
                        <Text fontSize="sm" fontWeight="600" color={colors.text}>{m.tipo}</Text>
                      </Flex>
                    </Box>
                    <Box as="td" px={6} py={4}><Text fontSize="sm" color={colors.text}>{m.tonelaje.toLocaleString()} TM</Text></Box>
                    <Box as="td" px={6} py={4}><Text fontSize="sm" color={colors.text}>{m.porcentaje}%</Text></Box>
                    <Box as="td" px={6} py={4}><Text fontSize="sm" color={colors.text}>{m.proveedores}</Text></Box>
                    <Box as="td" px={6} py={4}><TrendIndicator value={t.trend} direction={t.dir} /></Box>
                  </Box>
                )
              })}
            </Box>
          </Box>
        </Box>
      </MotionBox>
    </VStack>
  )
}
